import { classes, icons, Loader, RawLink } from '@portal/components'
import isEmpty from 'lodash/isEmpty'
import React, { memo, useCallback, useEffect, useMemo } from 'react'

import { useCollectionFilterSettings } from '../../store'
import { buildQueryForCollection, findCollectionName } from '../../utils'
import DataImportMenu from '../dataImporter/DataImportMenu'
import {
  useDQLEditorCollectionsContext,
  useDQLEditorContext,
} from '../dqlEditor/context'

const { ArrowUpRight, Book } = icons

const CollectionsList = memo(() => {
  const {
    appCollections,
    getCollectionDocumentCount,
    query,
    setQuery,
    activeView,
    accessToken,
    isLoading: isLoadingCollections,
  } = useDQLEditorContext()

  const { collectionCounts, setCollectionCounts } =
    useDQLEditorCollectionsContext()

  const { hideEmptyCollections, documentsLimit, hideInternalCollections } =
    useCollectionFilterSettings()

  useEffect(() => {
    const fetchCounts = async () => {
      // Still loading collections, don't fetch counts yet
      if (isLoadingCollections) {
        return
      }

      // Don't fetch if we don't have the necessary functions or access token
      if (
        !getCollectionDocumentCount ||
        !appCollections?.length ||
        !accessToken
      ) {
        return
      }

      const counts: Record<string, number | null> = {}

      const promises = appCollections.map(async (collection) => {
        try {
          const count = await getCollectionDocumentCount(collection)
          counts[collection] = count ?? null
        } catch (error) {
          counts[collection] = null
        }
      })

      await Promise.all(promises)
      setCollectionCounts(counts)
    }

    fetchCounts()
  }, [
    appCollections,
    getCollectionDocumentCount,
    accessToken,
    setCollectionCounts,
    isLoadingCollections,
  ])

  const filteredCollections = useMemo(() => {
    // Start with all collections, then filter out as needed
    let collectionsToDisplay = appCollections

    // Check if we have counts for all collections
    const countsLoaded = appCollections?.every(
      (collection) => collection in collectionCounts,
    )

    // Filter out empty collections only if counts are loaded
    if (hideEmptyCollections && countsLoaded) {
      collectionsToDisplay = collectionsToDisplay?.filter((collection) => {
        const count = collectionCounts[collection]
        return count === null || count > 0
      })
    }

    // Filter out internal collections
    if (hideInternalCollections) {
      collectionsToDisplay = collectionsToDisplay?.filter(
        (collection) => !collection.startsWith('__'),
      )
    }

    return collectionsToDisplay ?? []
  }, [
    appCollections,
    collectionCounts,
    hideEmptyCollections,
    hideInternalCollections,
  ])

  const activeCollectionName = useMemo(() => {
    const name = findCollectionName(query || '')
    return name?.replace(/^`|`$/g, '') // Removes backticks if present for comparison
  }, [query])

  const onQueryEntireCollection = useCallback(
    (collection: string) => {
      setQuery(
        buildQueryForCollection(collection, {
          limit: documentsLimit,
        }),
        activeView === 'collections', // Only execute the query if we are in the collections view
      )
    },
    [setQuery, activeView, documentsLimit],
  )

  // Check if counts are still being loaded for filtering purposes
  const countsLoading = useMemo(
    () =>
      hideEmptyCollections &&
      !!appCollections?.length &&
      !appCollections?.every((collection) => collection in collectionCounts),
    [hideEmptyCollections, appCollections, collectionCounts],
  )

  const isLoading = useMemo(
    () =>
      !appCollections ||
      (!isEmpty(appCollections) &&
        Object.keys(collectionCounts).length === 0) ||
      countsLoading,
    [appCollections, collectionCounts, countsLoading],
  )

  const hasNoCollections = useMemo(
    () =>
      isEmpty(appCollections) ||
      (!!appCollections?.length && isEmpty(filteredCollections)),
    [appCollections, filteredCollections],
  )

  // Only show content when we're done loading
  if (isLoading) {
    return (
      <div
        className="flex flex-1 flex-col items-center py-4"
        data-testid="collectionsLoading"
      >
        <Loader />
      </div>
    )
  }

  // At this point, loading is complete
  if (hasNoCollections) {
    return (
      <div className="p-2">
        <div className="flex flex-col items-center justify-center rounded-xl bg-background-surface px-2 py-4">
          <p className="text-foreground mb-1">
            You don&apos;t have any collections yet
          </p>
          <p className="mb-3 text-foreground-subtle">
            Import or insert via the SDK and API
          </p>

          <DataImportMenu
            title="Import your data"
            containerClass="w-full"
            buttonClass="w-full mb-3"
            hideIcon
          />

          <RawLink
            href="https://docs.ditto.live/dql"
            isBlank
            className="flex items-center gap-1"
          >
            <Book className="size-4 mr-1" />
            Read the docs
            <ArrowUpRight className="size-4" />
          </RawLink>
        </div>
      </div>
    )
  }

  return (
    <div role="list" className="flex-1 overflow-auto px-2 py-1">
      {filteredCollections?.map((collection) => (
        <div
          role="listitem"
          key={collection}
          className={classes(
            'flex w-full items-center justify-between rounded-lg py-1 pl-3 pr-1 text-base hover:bg-background-surface',
            activeCollectionName === collection ? 'bg-background-surface' : '',
          )}
        >
          <span
            className="line-clamp-1 w-full cursor-pointer px-1 py-2 outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline"
            role="button"
            tabIndex={0}
            aria-label={`Query entire ${collection} collection`}
            onClick={() => onQueryEntireCollection(collection)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onQueryEntireCollection(collection)
              }
            }}
          >
            {collection}
          </span>
        </div>
      ))}
    </div>
  )
})

CollectionsList.displayName = 'CollectionsList'

export default CollectionsList
